/**
 * Scenarios module - centralizes departure calculation logic
 *
 * This module provides a clean interface for calculating ship departure times
 * based on tide data, vessel specifications, and dock characteristics.
 */

export * from './calculations'

/**
 * Scenario type constants
 *
 * The 4 scenarios per PDF spec (Autumn 2025):
 * 1. latest_time - Heure maximale (no pilot)
 * 2. departure_range - Plage de départ (no pilot)
 * 3. departure_range_pilot_desc - Plage (Lamaneur desc.) - pilot descending only
 * 4. departure_range_pilot_updown - Plage (Lamaneur mont. et desc.) - pilot both directions
 */
export const SCENARIO_TYPES = {
    LATEST_TIME: 'latest_time',
    DEPARTURE_RANGE: 'departure_range',
    DEPARTURE_RANGE_PILOT_DESC: 'departure_range_pilot_desc',
    DEPARTURE_RANGE_PILOT_UPDOWN: 'departure_range_pilot_updown',
}

/**
 * All valid calculation method values (for validation)
 */
export const VALID_CALCULATION_METHODS = [
    SCENARIO_TYPES.LATEST_TIME,
    SCENARIO_TYPES.DEPARTURE_RANGE,
    SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC,
    SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN,
]

/**
 * Options for calculation method dropdown (uses i18n translation keys)
 */
export const calculOptions = [
    {
        id: SCENARIO_TYPES.LATEST_TIME,
        label: 'dock_page.latest_time',
    },
    {
        id: SCENARIO_TYPES.DEPARTURE_RANGE,
        label: 'dock_page.departure_range',
    },
    {
        id: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC,
        label: 'dock_page.departure_range_pilot_desc',
    },
    {
        id: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN,
        label: 'dock_page.departure_range_pilot_updown',
    },
]

/**
 * Maps scenario numbers (1-4) to their canonical calculation method types
 * Used to look up the correct label for a computed scenario number
 */
export const SCENARIO_TO_METHOD = {
    1: SCENARIO_TYPES.LATEST_TIME,
    2: SCENARIO_TYPES.DEPARTURE_RANGE,
    3: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC,
    4: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN,
}

/**
 * Check if a calculation method is a pilot scenario (3 or 4)
 * Only checks explicit scenario types - shoal is ignored for scenarios 1-2
 *
 * @param {string} calculationMethod - The calculation method
 * @returns {boolean}
 */
export function isPilotScenario(calculationMethod) {
    return (
        calculationMethod === SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC ||
        calculationMethod === SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN
    )
}

/**
 * Check if a calculation method is the dual-range pilot scenario (rising + descending)
 */
export function isDualRangePilotScenario(calculationMethod) {
    return calculationMethod === SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN
}

/**
 * Get the scenario number (1-4) from calculation method
 *
 * @param {string} calculationMethod - The calculation method
 * @param {number|null} dockShoal - Shoal depth (for backward compat with implicit detection)
 * @returns {number} Scenario number (1-4)
 */
export function getScenarioNumber(calculationMethod, dockShoal = null) {
    // Explicit new scenario types
    if (calculationMethod === SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC) return 3
    if (calculationMethod === SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN) return 4

    // Legacy implicit detection (backward compatibility)
    const hasShoalValue = dockShoal !== undefined && dockShoal !== null && dockShoal !== 0

    if (calculationMethod === SCENARIO_TYPES.LATEST_TIME) {
        // latest_time + shoal is DEPRECATED but still supported for existing data
        // Maps to scenario 3 (departure_range_pilot_desc) as fallback
        return hasShoalValue ? 3 : 1
    }

    // departure_range
    // With shoal: maps to scenario 3 (departure_range_pilot_desc) as default
    return hasShoalValue ? 3 : 2
}

/**
 * Determine scenario info based on calculation method and shoal
 *
 * Supports both:
 * - NEW explicit scenario types (departure_range_pilot_desc, departure_range_pilot_updown)
 * - OLD implicit detection (calculation_method + shoal presence)
 *
 * The 4 scenarios per PDF spec:
 * 1. latest_time (no shoal) - Single max departure time
 * 2. departure_range (no shoal) - Time range for departure
 * 3. departure_range_pilot_desc - Pilot section (descending only)
 * 4. departure_range_pilot_updown - Pilot section (rising + descending)
 *
 * @param {string} calculationMethod - Calculation method from dock/analysis
 * @param {number|null} dockShoal - Shoal depth (for backward compat)
 * @returns {Object} Scenario info
 */
export function getScenarioInfo(calculationMethod, dockShoal) {
    const scenarioNumber = getScenarioNumber(calculationMethod, dockShoal)

    // For explicit pilot scenarios, hasPilotSections is always true
    // For legacy scenarios, it depends on shoal presence
    const hasShoalValue = dockShoal !== undefined && dockShoal !== null && dockShoal !== 0
    const isExplicitPilotScenario = isPilotScenario(calculationMethod)

    switch (scenarioNumber) {
        case 1:
            return {
                scenarioNumber: 1,
                type: SCENARIO_TYPES.LATEST_TIME,
                hasPilotSections: false,
                showsStartTime: false,
                showsEndTime: true,
                hasDualRanges: false,
                description: 'Heure maximale / Maximum time',
            }
        case 2:
            return {
                scenarioNumber: 2,
                type: SCENARIO_TYPES.DEPARTURE_RANGE,
                hasPilotSections: false,
                showsStartTime: true,
                showsEndTime: true,
                hasDualRanges: false,
                description: 'Plage de départ / Departure window',
            }
        case 3:
            return {
                scenarioNumber: 3,
                type: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_DESC,
                hasPilotSections: true,
                showsStartTime: true,
                showsEndTime: true,
                hasDualRanges: false,
                description: 'Plage (Lamaneur desc.) / Window (harbour pilot desc.)',
                // For backward compat: mark if this was inferred from legacy data
                _inferredFromShoal: !isExplicitPilotScenario && hasShoalValue,
            }
        case 4:
            return {
                scenarioNumber: 4,
                type: SCENARIO_TYPES.DEPARTURE_RANGE_PILOT_UPDOWN,
                hasPilotSections: true,
                showsStartTime: true,
                showsEndTime: true,
                hasDualRanges: true,
                description: 'Plage (Lamaneur mont. et desc.) / Window (harbour pilot up & down)',
            }
        default:
            // Fallback to scenario 1
            return {
                scenarioNumber: 1,
                type: SCENARIO_TYPES.LATEST_TIME,
                hasPilotSections: false,
                showsStartTime: false,
                showsEndTime: true,
                hasDualRanges: false,
                description: 'Heure maximale / Maximum time',
            }
    }
}
